export function isSelfieCameraCaptureSupported() {
    return Boolean(
        globalThis.isSecureContext &&
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia
    );
}

export function createSelfieCameraCapture(
    uploadedCount,
    targetCount,
    showProgress,
    closeLabel,
    captureLabel,
    startLabel,
    onCaptured,
    onDismiss,
    onError
) {
    let stream = null;
    let busy = false;
    let captureEnabled = true;
    let previewReady = false;
    let stopped = false;

    const root = document.createElement("div");
    root.className = "web-selfie-camera";

    const video = document.createElement("video");
    video.className = "web-selfie-camera__video";
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;

    const scrim = document.createElement("div");
    scrim.className = "web-selfie-camera__scrim";

    const closeButton = document.createElement("button");
    closeButton.className = "web-selfie-camera__close";
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", closeLabel);
    closeButton.textContent = "\u00d7";

    const progress = document.createElement("div");
    progress.className = "web-selfie-camera__progress";

    const shutter = document.createElement("button");
    shutter.className = "web-selfie-camera__shutter";
    shutter.type = "button";
    shutter.setAttribute("aria-label", captureLabel);
    shutter.innerHTML = '<span aria-hidden="true"></span>';

    const startButton = document.createElement("button");
    startButton.className = "web-selfie-camera__start";
    startButton.type = "button";
    startButton.textContent = startLabel;
    startButton.hidden = true;

    root.appendChild(video);
    root.appendChild(scrim);
    root.appendChild(closeButton);
    if (showProgress) root.appendChild(progress);
    root.appendChild(shutter);
    root.appendChild(startButton);

    closeButton.addEventListener("click", () => onDismiss());
    shutter.addEventListener("click", () => capture());
    startButton.addEventListener("click", () => resumePreviewPlayback());
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    function setProgress(current, target) {
        progress.textContent = `${Math.min(current, target)}/${target}`;
    }

    function setCaptureEnabled(enabled) {
        captureEnabled = enabled;
        shutter.disabled = !enabled || busy || !previewReady;
        root.classList.toggle("web-selfie-camera--disabled", !enabled);
    }

    function setPreviewReady(ready) {
        previewReady = ready;
        startButton.hidden = ready;
        startButton.disabled = false;
        root.classList.toggle("web-selfie-camera--ready", ready);
        setCaptureEnabled(captureEnabled);
        if (!ready) focusSafely(startButton);
    }

    async function startPreviewPlayback() {
        try {
            await video.play();
        } catch (error) {
            if (stopped) return;
            if (isMediaPlaybackBlocked(error)) {
                setPreviewReady(false);
                return;
            }
            throw error;
        }
        if (!stopped) setPreviewReady(true);
    }

    async function resumePreviewPlayback() {
        if (stopped) return;
        startButton.disabled = true;
        try {
            await startPreviewPlayback();
        } catch (error) {
            if (stopped) return;
            onError(error && error.message ? error.message : String(error));
            onDismiss();
        }
    }

    async function start() {
        if (!isSelfieCameraCaptureSupported()) {
            throw new Error("Camera capture is not available in this browser context.");
        }
        if (stopped) return { started: false, permissionDenied: false, cameraUnavailable: false };

        injectStyles();
        document.body.appendChild(root);
        setProgress(uploadedCount, targetCount);
        setCaptureEnabled(captureEnabled);
        focusSafely(closeButton);

        let mediaStream;
        try {
            mediaStream = await openBestCameraStream();
        } catch (error) {
            if (isCameraPermissionDenied(error)) {
                return { started: false, permissionDenied: true, cameraUnavailable: false };
            }
            if (isCameraUnavailable(error)) {
                return { started: false, permissionDenied: false, cameraUnavailable: true };
            }
            throw error;
        }
        if (stopped) {
            stopStream(mediaStream);
            return { started: false, permissionDenied: false, cameraUnavailable: false };
        }

        stream = mediaStream;
        await tuneCameraTrack(stream);
        video.srcObject = stream;
        await startPreviewPlayback();
        if (stopped) {
            stop();
            return { started: false, permissionDenied: false, cameraUnavailable: false };
        }
        return { started: true, permissionDenied: false, cameraUnavailable: false };
    }

    function stop() {
        stopped = true;
        stopStream(stream);
        stream = null;
        video.srcObject = null;
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("pagehide", onPageHide);
        root.remove();
    }

    function stopStream(mediaStream) {
        if (!mediaStream) return;
        mediaStream.getTracks().forEach((track) => track.stop());
    }

    async function capture() {
        if (busy || !captureEnabled) return;
        if (!video.videoWidth || !video.videoHeight) {
            onError("Camera preview is not ready yet.");
            return;
        }

        busy = true;
        setCaptureEnabled(captureEnabled);
        playShutterFeedback();

        try {
            const blob = showProgress
                ? await capturePhotoBlob()
                : await capturePhotoFromCanvas();
            const file = new File([blob], `selfie_${Date.now()}.jpg`, {
                type: blob.type || "image/jpeg",
                lastModified: Date.now()
            });
            onCaptured(file);
            if (!showProgress) {
                onDismiss();
            }
        } catch (error) {
            onError(error && error.message ? error.message : String(error));
        } finally {
            busy = false;
            setCaptureEnabled(captureEnabled);
        }
    }

    async function capturePhotoBlob() {
        const imageCaptureBlob = await takePhotoFromImageCapture();
        if (imageCaptureBlob) return imageCaptureBlob;

        const grabbedFrameBlob = await grabFrameFromImageCapture();
        if (grabbedFrameBlob) return grabbedFrameBlob;

        return capturePhotoFromCanvas();
    }

    async function takePhotoFromImageCapture() {
        if (!stream || typeof globalThis.ImageCapture !== "function") return null;
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== "live") return null;
        try {
            const imageCapture = new globalThis.ImageCapture(videoTrack);
            const photoSettings = await buildPhotoSettings(imageCapture);
            try {
                return await imageCapture.takePhoto(photoSettings);
            } catch (_) {
                return await imageCapture.takePhoto();
            }
        } catch (_) {
            return null;
        }
    }

    async function grabFrameFromImageCapture() {
        if (!stream || typeof globalThis.ImageCapture !== "function") return null;
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== "live") return null;
        try {
            const imageCapture = new globalThis.ImageCapture(videoTrack);
            const imageBitmap = await imageCapture.grabFrame();
            return imageBitmapToBlob(imageBitmap);
        } catch (_) {
            return null;
        }
    }

    function capturePhotoFromCanvas() {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Unable to create capture canvas.");
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        return canvasToBlob(canvas);
    }

    function onKeyDown(event) {
        if (event.key === "Escape") {
            event.preventDefault();
            onDismiss();
        }
    }

    function onVisibilityChange() {
        if (stream && document.visibilityState === "hidden") {
            onDismiss();
        }
    }

    function onPageHide() {
        onDismiss();
    }

    return {
        start,
        stop,
        setProgress,
        setCaptureEnabled
    };
}

function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Unable to create selfie image."));
        }, "image/jpeg", 0.96);
    });
}

async function openBestCameraStream() {
    const constraintsList = buildCameraConstraintFallbacks();
    let lastError = null;

    for (const videoConstraints of constraintsList) {
        try {
            return await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: videoConstraints
            });
        } catch (error) {
            if (!shouldTryNextCameraConstraint(error)) throw error;
            lastError = error;
        }
    }

    throw lastError || new Error("Unable to start camera.");
}

function buildCameraConstraintFallbacks() {
    const pixelRatio = Math.min(globalThis.devicePixelRatio || 1, 3);
    const viewportWidth = Math.max(globalThis.innerWidth || 0, document.documentElement.clientWidth || 0);
    const viewportHeight = Math.max(globalThis.innerHeight || 0, document.documentElement.clientHeight || 0);
    const shortEdge = Math.max(720, Math.ceil(Math.min(viewportWidth, viewportHeight) * pixelRatio));
    const longEdge = Math.max(1280, Math.ceil(Math.max(viewportWidth, viewportHeight) * pixelRatio));
    const idealWidth = Math.min(longEdge, 2560);
    const idealHeight = Math.min(shortEdge, 1440);

    return [
        {
            facingMode: { ideal: "user" },
            width: { min: 1280, ideal: idealWidth },
            height: { min: 720, ideal: idealHeight },
            aspectRatio: { ideal: 16 / 9 },
            frameRate: { ideal: 30 },
            resizeMode: "none"
        },
        {
            facingMode: { ideal: "user" },
            width: { min: 1280, ideal: idealWidth },
            height: { min: 720, ideal: idealHeight },
            frameRate: { ideal: 30 }
        },
        {
            facingMode: "user",
            width: { ideal: idealWidth },
            height: { ideal: idealHeight },
            frameRate: { ideal: 30 }
        },
        true
    ];
}

function shouldTryNextCameraConstraint(error) {
    return error?.name === "OverconstrainedError" || error?.name === "ConstraintNotSatisfiedError";
}

function isCameraPermissionDenied(error) {
    return error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError";
}

function isCameraUnavailable(error) {
    return error?.name === "NotReadableError" || error?.name === "TrackStartError";
}

function isMediaPlaybackBlocked(error) {
    return error?.name === "NotAllowedError";
}

async function tuneCameraTrack(mediaStream) {
    const tracks = mediaStream && typeof mediaStream.getVideoTracks === "function"
        ? mediaStream.getVideoTracks()
        : [];
    const track = tracks[0];
    if (!track || typeof track.applyConstraints !== "function") return;

    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints
        ? navigator.mediaDevices.getSupportedConstraints()
        : {};
    const advancedConstraints = {};

    if (supportedConstraints.focusMode) advancedConstraints.focusMode = "continuous";
    if (supportedConstraints.exposureMode) advancedConstraints.exposureMode = "continuous";
    if (supportedConstraints.whiteBalanceMode) advancedConstraints.whiteBalanceMode = "continuous";

    if (Object.keys(advancedConstraints).length === 0) return;

    try {
        await track.applyConstraints({ advanced: [advancedConstraints] });
    } catch (_) {
        // Fixed-focus webcams, including many laptop cameras, commonly reject these hints.
    }
}

async function buildPhotoSettings(imageCapture) {
    if (typeof imageCapture.getPhotoCapabilities !== "function") return {};

    try {
        const capabilities = await imageCapture.getPhotoCapabilities();
        const settings = {};
        const imageWidth = preferredPhotoDimension(capabilities.imageWidth, 2560);
        const imageHeight = preferredPhotoDimension(capabilities.imageHeight, 1920);

        if (imageWidth) settings.imageWidth = imageWidth;
        if (imageHeight) settings.imageHeight = imageHeight;

        return settings;
    } catch (_) {
        return {};
    }
}

function preferredPhotoDimension(range, maxPreferred) {
    if (!range || typeof range.max !== "number") return null;
    const min = typeof range.min === "number" ? range.min : 0;
    return Math.max(min, Math.min(range.max, maxPreferred));
}

async function imageBitmapToBlob(imageBitmap) {
    try {
        const canvas = document.createElement("canvas");
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Unable to create capture canvas.");
        context.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);
        return await canvasToBlob(canvas);
    } finally {
        imageBitmap.close?.();
    }
}

function playShutterFeedback() {
    try {
        if (navigator.vibrate) navigator.vibrate(28);
    } catch (_) {
        // Ignore feedback failures.
    }

    try {
        const AudioContextClass = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AudioContextClass) return;

        const audioContext = new AudioContextClass();
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const now = audioContext.currentTime;

        oscillator.type = "triangle";
        oscillator.frequency.setValueAtTime(880, now);
        oscillator.frequency.exponentialRampToValueAtTime(420, now + 0.055);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.22, now + 0.006);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.075);

        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.08);
        oscillator.onended = () => audioContext.close();
    } catch (_) {
        // Ignore feedback failures.
    }
}

function focusSafely(element) {
    try {
        element.focus({ preventScroll: true });
    } catch (_) {
        element.focus();
    }
}

function injectStyles() {
    if (document.getElementById("web-selfie-camera-styles")) return;

    const style = document.createElement("style");
    style.id = "web-selfie-camera-styles";
    style.textContent = `
        .web-selfie-camera {
            position: fixed;
            inset: 0;
            z-index: 2147483647;
            overflow: hidden;
            background: #000;
            color: #fff;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            touch-action: none;
        }

        .web-selfie-camera__video {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transform: scaleX(-1);
            opacity: 0;
            transition: opacity 140ms ease;
        }

        .web-selfie-camera--ready .web-selfie-camera__video {
            opacity: 1;
        }

        .web-selfie-camera__scrim {
            position: absolute;
            inset: 0;
            pointer-events: none;
            background:
                linear-gradient(to bottom, rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0) 28%),
                linear-gradient(to top, rgba(0, 0, 0, 0.54), rgba(0, 0, 0, 0) 36%);
        }

        .web-selfie-camera__close,
        .web-selfie-camera__progress,
        .web-selfie-camera__shutter,
        .web-selfie-camera__start {
            position: absolute;
            box-sizing: border-box;
            border: 0;
            font: inherit;
        }

        .web-selfie-camera__close {
            top: max(16px, env(safe-area-inset-top));
            left: max(16px, env(safe-area-inset-left));
            width: 48px;
            height: 48px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.9);
            color: #000;
            font-size: 34px;
            line-height: 44px;
            cursor: pointer;
        }

        .web-selfie-camera__progress {
            top: max(18px, env(safe-area-inset-top));
            right: max(16px, env(safe-area-inset-right));
            min-width: 54px;
            padding: 8px 14px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.92);
            color: #000;
            text-align: center;
            font-size: 14px;
            font-weight: 700;
        }

        .web-selfie-camera__shutter {
            left: 50%;
            bottom: max(34px, calc(env(safe-area-inset-bottom) + 24px));
            width: 86px;
            height: 86px;
            padding: 0;
            border-radius: 999px;
            border: 4px solid rgba(255, 255, 255, 0.96);
            background: rgba(255, 255, 255, 0.12);
            transform: translateX(-50%);
            cursor: pointer;
        }

        .web-selfie-camera__shutter span {
            display: block;
            width: 66px;
            height: 66px;
            margin: 6px;
            border-radius: 999px;
            background: #2fb7e8;
        }

        .web-selfie-camera__shutter:disabled {
            opacity: 0.55;
            cursor: default;
        }

        .web-selfie-camera__start {
            top: 50%;
            left: 50%;
            min-height: 52px;
            max-width: calc(100% - 48px);
            padding: 14px 24px;
            border-radius: 999px;
            background: #006d9c;
            color: #fff;
            font-size: 16px;
            font-weight: 700;
            transform: translate(-50%, -50%);
            cursor: pointer;
        }

        .web-selfie-camera__start:disabled {
            opacity: 0.7;
            cursor: wait;
        }

        .web-selfie-camera__start[hidden] {
            display: none;
        }
    `;
    document.head.appendChild(style);
}
